import type { Coordinate, Door, LocationProfile, MapData, PointOfInterest, Space } from "@mappedin/mappedin-js";
import { config } from "./config";
import { resolveLocalAsset, slugify } from "./local-assets";

/**
 * One searchable entry in the kiosk directory.
 *
 * Mappedin's Maker data model separates the physical polygon (Space, drawn
 * on the floor) from the business info attached to it (LocationProfile —
 * name, description, logo, categories). A stand is only meaningful to a
 * visitor once both exist, so we flatten them into one record here and
 * drop any LocationProfile that has no Space to route to.
 */
export interface StandEntry {
  id: string;
  name: string;
  description?: string;
  categoryNames: string[];
  /**
   * The location's logo/icon image, if one was uploaded for it (or, failing
   * that, whatever the SDK falls back to — its category icon, per
   * `LocationProfile.icon`'s own doc comment: "Will be either a logo,
   * category, or image"). Undefined when nothing is set anywhere.
   */
  iconUrl?: string;
  /**
   * The first of the location's uploaded pictures (`LocationProfile.images`),
   * distinct from its logo — undefined when none were uploaded. Shown in the
   * place-card detail grid; a *different* picture from the 3D floor "case"
   * image (`public/stands/<externalId>.*`, local-only, never this field).
   */
  photoUrl?: string;
  /** The routable target — pass this straight into mapView.getDirections(). */
  space: Space;
  location: LocationProfile;
}

export function buildDirectory(mapData: MapData): StandEntry[] {
  const locations = mapData.getByType("location-profile");
  const entries: StandEntry[] = [];

  // `location.icon`/`.images[n]` are getters that synchronously build a
  // signed Mappedin CDN URL — which needs the SDK's token manager, and in
  // offline mode (`hydrateFromLocalBundle`, see mappedin.ts) that manager
  // never readies since there's no internet to fetch a token with. Calling
  // them anyway logs a pair of SDK console errors per location AND is
  // wasted work either way: `resolveLocalDirectoryAssets` right after this
  // discards or replaces whatever value ends up here once mapMode is
  // "offline" — see `resolveLocalAsset`'s own comment.
  const useRemoteIcons = config.mapMode !== "offline";

  for (const location of locations) {
    const space = location.spaces[0];
    if (!space) continue; // Not assigned to a stand yet in Mappedin's admin — not searchable.
    entries.push({
      id: location.id,
      name: location.name,
      description: location.description,
      categoryNames: location.categories.map((c) => c.name),
      iconUrl: useRemoteIcons ? location.icon?.url : undefined,
      photoUrl: useRemoteIcons ? location.images[0]?.url : undefined,
      space,
      location,
    });
  }

  return entries.sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Swaps each entry's `iconUrl`/`photoUrl` for a matching local file (a
 * manifest lookup — see local-assets.ts) when one exists, falling back to
 * the original Mappedin-hosted URL otherwise. Those remote URLs sit behind
 * a short-lived, SAS-signed CDN link that itself needs a live API call to
 * construct, so they never load once the kiosk is offline — this is what
 * lets a stand's logo/detail-photo keep showing regardless. Note this is a
 * *separate* local file from the 3D floor case image (`public/stands/`,
 * handled entirely inside `renderLocationPhotos` in mappedin.ts) — the two
 * are different pictures. Mutates `entries` in place; every stand resolves
 * in parallel since each is independent (and the manifest itself is only
 * ever fetched once regardless of how many resolve concurrently — see
 * `loadManifest` in local-assets.ts). Call this before anything reads
 * `iconUrl`/`photoUrl` (label placement, logo markers, the place card) so
 * they see the resolved URL.
 */
export async function resolveLocalDirectoryAssets(entries: StandEntry[]): Promise<void> {
  await Promise.all(
    entries.map(async (entry) => {
      // Tried in order — see `resolveLocalAsset`'s own comment for why this
      // needs to be a list rather than one `||`-picked key: a location with
      // no External ID set anywhere still has its own stable internal `id`
      // (always present), which is what `fetch-full-mvf.mjs` falls back to
      // naming its saved logo/photo file after. Without this, an exhibitor
      // who uploaded a logo but never got an External ID assigned would
      // never have it sync locally, and so it'd disappear the moment the
      // kiosk goes offline even though the file exists on disk.
      const keys = [entry.location.externalId, entry.space.externalId, entry.location.id];

      const [iconUrl, photoUrl] = await Promise.all([
        resolveLocalAsset("logos", keys, entry.iconUrl),
        resolveLocalAsset("photos", keys, entry.photoUrl),
      ]);
      entry.iconUrl = iconUrl;
      entry.photoUrl = photoUrl;
    }),
  );
}

export function allCategories(entries: StandEntry[]): string[] {
  const names = new Set<string>();
  for (const entry of entries) {
    for (const name of entry.categoryNames) names.add(name);
  }
  return [...names].sort((a, b) => a.localeCompare(b));
}

/**
 * Maps each category name to its icon URL, when one has been uploaded for
 * that category in the Mappedin admin (Locations → Categories). Categories
 * with no icon set are simply absent from the map — callers should fall
 * back to a text-only chip in that case.
 */
export function categoryIconMap(mapData: MapData): Map<string, string> {
  const map = new Map<string, string>();
  for (const category of mapData.getByType("location-category")) {
    if (category.icon) map.set(category.name, category.icon);
  }
  return map;
}

/**
 * Swaps each category's icon URL for a matching local file (a manifest
 * lookup, keyed by `slugify(name)` — see local-assets.ts) when one exists,
 * same reasoning as `resolveLocalDirectoryAssets` — a category's icon is
 * just as much a remote, SAS-signed Mappedin CDN URL as a location's own
 * logo/photo. Mutates `icons` in place.
 */
export async function resolveLocalCategoryIcons(icons: Map<string, string>): Promise<void> {
  await Promise.all(
    [...icons.entries()].map(async ([name, remoteUrl]) => {
      const resolved = await resolveLocalAsset("categoryIcons", slugify(name), remoteUrl);
      // `resolved` comes back `undefined` (not just "no local file") when
      // offline with no local file — see `resolveLocalAsset`'s own comment
      // for why keeping the original remote URL in that case would be a
      // broken image on the map instead of the "no icon" text-chip
      // fallback this file's own doc comment already promises callers.
      if (resolved) icons.set(name, resolved);
      else icons.delete(name);
    }),
  );
}

/**
 * Ranked substring search: prefix matches on the name outrank interior
 * matches, which outrank a hit only in the description/category. Good
 * enough for a few hundred exhibitors typed on a touch keyboard; swap in
 * `mapData.Search` if the venue's directory grows large enough to need
 * fuzzy/typo-tolerant matching.
 */
export function searchDirectory(entries: StandEntry[], rawTerm: string): StandEntry[] {
  const term = rawTerm.trim().toLowerCase();
  if (!term) return [];

  const scored: { entry: StandEntry; score: number }[] = [];
  for (const entry of entries) {
    const name = entry.name.toLowerCase();
    let score = -1;
    if (name.startsWith(term)) score = 3;
    else if (name.includes(term)) score = 2;
    else if (entry.categoryNames.some((c) => c.toLowerCase().includes(term))) score = 1;
    else if (entry.description?.toLowerCase().includes(term)) score = 1;

    if (score >= 0) scored.push({ entry, score });
  }

  scored.sort((a, b) => b.score - a.score || a.entry.name.localeCompare(b.entry.name));
  return scored.map((s) => s.entry);
}

export function byCategory(entries: StandEntry[], categoryName: string): StandEntry[] {
  return entries.filter((e) => e.categoryNames.includes(categoryName));
}

/**
 * A kiosk's own fixed physical position, when it's anchored to a bare Space
 * or Point of Interest that isn't (or couldn't be matched to) a searchable
 * exhibitor stand — see `findKioskAnchor`. Distinguished from `StandEntry`
 * by having `target` instead of `space`; `routeOriginTarget` reads either.
 */
export interface KioskAnchor {
  id: string;
  name: string;
  target: Space | PointOfInterest;
}

/**
 * A visitor's own live GPS fix, used as a route's starting point instead of
 * any stand — for someone standing in an aisle or open area rather than at
 * a named exhibitor. See `selectCurrentLocation` in main.ts.
 */
export interface LiveLocationAnchor {
  id: "current-location";
  name: string;
  target: Coordinate;
}

/**
 * An emergency exit. In this venue's Mappedin data each exit is a named
 * door ("Emergency Exit-1 (Hall-5)") with a location profile of the same
 * name linked to that door, not to any space, so `buildDirectory` (stands
 * only) never lists them. Routed to, and marked on the map, at the door.
 */
export interface ExitAnchor {
  id: string;
  name: string;
  target: Door;
  kind: "exit";
}

/** Anything a route can start or end at: a searchable stand, a kiosk's own point/space anchor, an exit door, or a visitor's live GPS fix. */
export type RouteOrigin = StandEntry | KioskAnchor | ExitAnchor | LiveLocationAnchor;

/** Every kind of map object a route can start/end at. */
export type MapTarget = Space | PointOfInterest | Coordinate | Door;

/** True for a `RouteOrigin` that's an emergency exit door. */
export function isExitAnchor(origin: RouteOrigin | null | undefined): origin is ExitAnchor {
  return Boolean(origin && "kind" in origin && origin.kind === "exit");
}

/**
 * All emergency exits: location profiles tied to a door rather than a space,
 * plus any exit-named door without one, sorted by name.
 */
export function buildExitDirectory(mapData: MapData): ExitAnchor[] {
  const exits = new Map<string, ExitAnchor>();
  for (const location of mapData.getByType("location-profile")) {
    const door = location.doors[0];
    if (location.spaces.length > 0 || !door || !location.name.trim()) continue;
    exits.set(door.id, { id: location.id, name: location.name.trim(), target: door, kind: "exit" });
  }
  for (const door of mapData.getByType("door")) {
    if (exits.has(door.id) || !/exit/i.test(door.name ?? "")) continue;
    exits.set(door.id, { id: door.id, name: door.name.trim(), target: door, kind: "exit" });
  }
  // Grouped by hall ("… (Hall-2)"), then by exit number within it.
  const hallOf = (name: string) => Number(name.match(/hall\W*(\d+)/i)?.[1] ?? 99);
  return [...exits.values()].sort(
    (a, b) => hallOf(a.name) - hallOf(b.name) || a.name.localeCompare(b.name, undefined, { numeric: true }),
  );
}

/** Exits whose name contains every word of `term` (so "exit hall 5" or "5" both work). */
export function searchExits(exits: ExitAnchor[], term: string): ExitAnchor[] {
  const words = term.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];
  return exits.filter((e) => words.every((w) => e.name.toLowerCase().includes(w)));
}

/** True for a `RouteOrigin` that's a full, searchable directory entry (has a routable `.space` and exhibitor data) rather than a bare kiosk anchor. */
export function isStandEntry(origin: RouteOrigin): origin is StandEntry {
  return "space" in origin;
}

/** True for a `RouteOrigin` backed by a visitor's live GPS fix rather than any stand or kiosk anchor. */
export function isLiveLocationAnchor(origin: RouteOrigin | null | undefined): origin is LiveLocationAnchor {
  return origin?.id === "current-location";
}

/** The actual routable/anchorable map object behind a `RouteOrigin`, regardless of which variant it is. */
export function routeOriginTarget(origin: RouteOrigin): MapTarget {
  return isStandEntry(origin) ? origin.space : origin.target;
}

/**
 * Resolves `externalId` (VITE_KIOSK_LOCATION_EXTERNAL_ID) to this kiosk's
 * physical anchor. Tries a Point of Interest first — a precise point is
 * often the better fit for where a kiosk unit actually stands (e.g. in an
 * open aisle, not inside any one exhibitor's stand) — then falls back to a
 * Space, matching it into the full directory entry when that stand also has
 * an exhibitor profile so "This Kiosk" shows its real name/logo.
 */
export function findKioskAnchor(mapData: MapData, externalId: string, directory: StandEntry[]): RouteOrigin | undefined {
  const poi = mapData.getByExternalId("point-of-interest", externalId)[0];
  if (poi) return { id: poi.id, name: poi.name, target: poi };

  const space = mapData.getByExternalId("space", externalId)[0];
  if (!space) return undefined;
  return directory.find((e) => e.space.id === space.id) ?? { id: space.id, name: space.name, target: space };
}

/**
 * Resolves `externalId` to the bare map object it identifies — a Point of
 * Interest checked first, then a Space — with no directory matching or
 * fallback. Used to render an icon for every kiosk stand across the venue
 * (see VITE_ALL_KIOSK_EXTERNAL_IDS), where only the anchorable target
 * matters, not exhibitor data. `findKioskAnchor` is the richer version used
 * for this device's own location.
 */
export function findAnchorTarget(mapData: MapData, externalId: string): Space | PointOfInterest | undefined {
  const poi = mapData.getByExternalId("point-of-interest", externalId)[0];
  if (poi) return poi;
  return mapData.getByExternalId("space", externalId)[0];
}
